import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

import { broadcastAuthEvent, subscribeAuthBroadcast } from '../lib/authBroadcast';
import { BRAK_STANU_MFA, pobierzStanMfa, type StanMfa } from '../lib/dwuskladnikowe';
import { resetSession } from '../lib/sessionReset';
import { supabase } from '../lib/supabase';
import { biegnacaSesja } from '../lib/supportSession';
import { useModuleSubscriptionStore } from '../stores/moduleSubscriptionStore';
import type {
  AuthContextType,
  UserProfile,
  UserOrganization,
  UserRole,
  CompanyRegistrationData,
  ImpersonatingPartner,
  OrganizationsStatus,
} from '../types/auth';

import { runSignOutSequence } from './signOutSequence';

const AUTH_INIT_TIMEOUT_MS = 8_000;

/**
 * Tryb podglądu partnera przeżywa przeładowanie karty.
 *
 * Wcześniej siedział wyłącznie w stanie Reacta, więc F5 na dowolnym ekranie
 * panelu partnera cicho wracało do kontekstu super-admina: baner znikał, a
 * ekrany — już bez wskazanego partnera — pokazywały „Nie znaleziono profilu
 * partnera". `sessionStorage`, a nie `localStorage`, bo podgląd ma ginąć wraz
 * z kartą i nie przenosić się na kolejne sesje.
 */
const KLUCZ_PODGLADU = 'insolitum.podglad-partnera';

function odczytajPodglad(): ImpersonatingPartner | null {
  try {
    const raw = sessionStorage.getItem(KLUCZ_PODGLADU);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonatingPartner;
    return parsed?.id && parsed?.organizationId ? parsed : null;
  } catch {
    return null;
  }
}

function zapiszPodglad(podglad: ImpersonatingPartner | null) {
  try {
    if (podglad) sessionStorage.setItem(KLUCZ_PODGLADU, JSON.stringify(podglad));
    else sessionStorage.removeItem(KLUCZ_PODGLADU);
  } catch {
    /* prywatny tryb przeglądarki — podgląd po prostu nie przeżyje odświeżenia */
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<UserOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Trójstan zamiast `organizationsLoaded: boolean` — patrz komentarz przy
  // `OrganizationsStatus`. `organizationsLoaded` niżej jest z tego WYPROWADZONE,
  // a nie trzymane osobno, żeby dwa stany nie mogły się rozjechać.
  const [organizationsStatus, setOrganizationsStatus] = useState<OrganizationsStatus>('loading');
  const organizationsLoaded = organizationsStatus === 'loaded';
  const [impersonatingPartner, setImpersonatingPartner] = useState<ImpersonatingPartner | null>(
    () => odczytajPodglad()
  );
  // Organizacja partnera zalogowanego (właściciel albo członek zespołu bez roli
  // `partner`). Ustawiana z `partner_kontekst()` przy rozruchu — to jedyny
  // sygnał, po którym bramka panelu rozpozna pracownika partnera.
  const [partnerOrgId, setPartnerOrgId] = useState<string | null>(null);
  // 2FA: poziom bieżącej sesji i to, czy konto ma zweryfikowany czynnik —
  // z JWT (bez sieci). Odświeżane po każdym zdarzeniu auth, które zmienia
  // token, i po `MFA_CHALLENGE_VERIFIED`. ProtectedRoute robi z tego bramkę.
  const [stanMfa, setStanMfa] = useState<StanMfa>(BRAK_STANU_MFA);
  const previousOrgRef = useRef<UserOrganization | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  // Pokolenie sesji — patrz `fetchUserData`. Rośnie przy KAŻDYM wyczyszczeniu
  // stanu (wylogowanie w tej karcie, w innej karcie, sprzątanie po błędzie
  // odświeżania) i przy zmianie użytkownika.
  const pokolenieSesjiRef = useRef(0);

  const odswiezMfa = useCallback(async () => {
    try {
      setStanMfa(await pobierzStanMfa());
    } catch (error) {
      console.warn('Nie udało się odczytać poziomu 2FA sesji', error);
    }
  }, []);

  const isRefreshTokenError = (error: unknown) => {
    if (!error) return false;
    const message = error instanceof Error ? error.message : String(error);
    return message.includes('Invalid Refresh Token') || message.includes('Refresh Token Not Found');
  };

  const clearLocalAuthState = useCallback(() => {
    pokolenieSesjiRef.current += 1;
    setUser(null);
    setOrganizations([]);
    setCurrentOrganization(null);
    setPartnerOrgId(null);
    setOrganizationsStatus('loading');
    currentUserIdRef.current = null;
    previousOrgRef.current = null;
    setImpersonatingPartner(null);
    zapiszPodglad(null);
  }, []);

  const resetAuthState = useCallback(
    async (reason: string) => {
      try {
        // Lokalnie: to sprzątanie po błędzie odświeżania w TEJ karcie, a nie
        // decyzja o sesjach na innych urządzeniach (patrz `signOut` niżej).
        await supabase.auth.signOut({ scope: 'local' });
      } catch (error) {
        console.warn('Failed to sign out after auth error', { reason, error });
      } finally {
        await resetSession({ reason, isUserChange: false });
        clearLocalAuthState();
      }
    },
    [clearLocalAuthState]
  );

  const fetchUserData = useCallback(
    async (userId: string, pokolenie: number, authEmail?: string, authFullName?: string) => {
      // ODPOWIEDŹ SPÓŹNIONA PO WYLOGOWANIU NIE MA PRAWA WSKRZESIĆ SESJI.
      // `pokolenie` to stan z chwili, w której wczytywanie ZLECONO (zdarzenie
      // auth, start, „Spróbuj ponownie") — nie z chwili, w której ruszyło, bo
      // `SIGNED_OUT` może przyjść także między zleceniem a startem. Po każdym
      // `await` sprawdzamy, czy sesja, dla której pytaliśmy, jeszcze trwa.
      // Bez tego odpowiedź z `users`, która przyszła po `SIGNED_OUT`, robiła
      // `setUser(profile)` już po `clearLocalAuthState()`, zapytanie
      // o organizacje szło jako anon (42501 → status `error`), a
      // `ProtectedRoute` zamiast /login pokazywał „Nie udało się sprawdzić
      // Twoich uprawnień" (CI 15.10.2026, `auth.spec.ts › should logout
      // successfully`; test: `wylogowanieWTrakcieWczytywania.test.tsx`).
      const nieaktualne = () => pokolenieSesjiRef.current !== pokolenie;
      if (nieaktualne()) return;
      // Ponowna próba po błędzie ma wrócić do „nie wiemy jeszcze", a nie zostać
      // w „nie wiemy wcale". Aktualizacja funkcyjna, żeby nie cofać stanu
      // `loaded` do `loading` przy odświeżaniu w tle — to migałoby spinnerem na
      // działającej już stronie.
      setOrganizationsStatus((current) => (current === 'error' ? 'loading' : current));
      try {
        const profileRes = await supabase.from('users').select('*').eq('id', userId).single();
        if (nieaktualne()) return;
        let profile = profileRes.data;
        const profileError = profileRes.error;

        if (profileError?.code === 'PGRST116') {
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
              id: userId,
              email: authEmail || '',
              full_name: authFullName || authEmail?.split('@')[0] || 'User',
              preferred_language: 'pl',
              is_super_admin: false,
            })
            .select()
            .single();
          if (nieaktualne()) return;

          if (createError) {
            console.error('Error creating profile:', createError);
            setUser({
              id: userId,
              email: authEmail || '',
              full_name: authFullName || 'User',
              preferred_language: 'pl',
              is_super_admin: false,
              created_at: new Date().toISOString(),
            });
          } else {
            profile = newProfile;
            setUser(profile as UserProfile);
          }
        } else if (profileError) {
          console.error('Error fetching profile:', profileError);
          setUser({
            id: userId,
            email: authEmail || '',
            full_name: authFullName || 'User',
            preferred_language: 'pl',
            is_super_admin: false,
            created_at: new Date().toISOString(),
          });
        } else {
          setUser(profile as UserProfile);
        }

        const { data: userOrgs, error: orgsError } = await supabase
          .from('user_organizations')
          .select(
            `
          *,
          organization:organizations(*)
        `
          )
          .eq('user_id', userId);
        if (nieaktualne()) return;

        if (orgsError) {
          console.error('Error fetching organizations:', orgsError);
          // Wcześniej było tu samo `return`, przez co status zostawał na
          // „loading" na zawsze, a `organizations` puste. Gdy bezpiecznik zdjął
          // spinner, ProtectedRoute odczytywał pustą listę jako „brak
          // uprawnień" i pokazywał /unauthorized użytkownikowi, który
          // uprawnienia ma. Zapisujemy więc, że odpowiedzi NIE ZNAMY.
          setOrganizationsStatus('error');
          return;
        }

        let orgsData = userOrgs as UserOrganization[];

        // ── Rozruch konta partnera ────────────────────────────────────────
        //
        // Profil partnera zakłada super-admin, zanim partner założy konto, więc
        // świeżo zalogowany partner nie ma jeszcze ANI dowiązanego `user_id`,
        // ANI członkostwa w swojej organizacji. `partner_kontekst()` (migracje
        // 463/465) robi jedno i drugie — ale musi zostać wywołane DOKŁADNIE
        // TUTAJ, a nie z hooka strony panelu.
        //
        // Powód jest taki, że `/partner` stoi za `ProtectedRoute requiredPanel="partner"`,
        // a ta bramka pyta `canAccessPanel('partner')`, czyli o rolę `partner`
        // na liście organizacji. Konto bez członkostwa dostaje `/unauthorized`
        // zanim jakikolwiek komponent panelu się zamontuje — samonaprawa
        // wywoływana z panelu nigdy by się nie odpaliła. Klasyczne jajko
        // i kura: żeby wejść, trzeba mieć członkostwo; żeby je dostać, trzeba
        // było wejść.
        //
        // Wołamy tylko wtedy, gdy roli `partner` faktycznie brakuje — dla
        // zwykłego najemcy to jedno dodatkowe zapytanie na sesję, dla partnera
        // jedyna droga do panelu. Błąd jest nieszkodliwy: konto po prostu
        // zostaje przy dotychczasowych organizacjach.
        // Organizacja partnera, do której należy to konto. Dla właściciela z rolą
        // `partner` bierzemy ją wprost z listy; dla pracownika (rola
        // employee/manager/admin w organizacji partnerskiej) i dla właściciela
        // przed rozruchem — z `partner_kontekst()`.
        let partnerOrg: string | null =
          orgsData.find((o) => o.role === 'partner')?.organization_id ?? null;

        if (!orgsData.some((o) => o.role === 'partner')) {
          try {
            const { data: kontekst } = await supabase.rpc('partner_kontekst', {
              p_partner_id: null,
            });
            const wiersz = (kontekst as { organization_id: string | null }[] | null)?.[0];

            if (wiersz?.organization_id) {
              partnerOrg = wiersz.organization_id;
              const { data: poRozruchu } = await supabase
                .from('user_organizations')
                .select(`*, organization:organizations(*)`)
                .eq('user_id', userId);
              if (poRozruchu) orgsData = poRozruchu as UserOrganization[];
            }
          } catch (err) {
            console.warn('Rozruch konta partnera nie powiódł się:', err);
          }
        }

        if (nieaktualne()) return;
        setPartnerOrgId(partnerOrg);
        setOrganizations(orgsData);
        setOrganizationsStatus('loaded');

        const activeOrgs = orgsData.filter((o) => o.status === 'active' || o.status === 'pending');

        // Trwająca sesja wsparcia wskazuje organizację MOCNIEJ niż `is_default`.
        // Partner, który wykupił kod klienta, dostaje u niego czasowe
        // członkostwo — ale jest ono zwykłym wierszem `user_organizations`
        // z rolą `partner` i bez `is_default`, więc domyślny wybór wracał do
        // organizacji samego partnera i wejście na konto klienta kończyło się
        // z powrotem w panelu partnera. Zgoda była wtedy do niczego.
        const wsparcie = biegnacaSesja();
        const orgWsparcia = wsparcie
          ? orgsData.find((o) => o.organization_id === wsparcie.organizationId)
          : undefined;

        // Organizacja partnera nie jest najemcą — jej miejscem jest panel
        // partnera (`/partner`), a nie panel firmy. Członek zespołu partnera,
        // który ma też firmę (choćby pokazową, migracja 883), ląduje w `/admin`
        // na tej firmie, a nie na „panelu najemcy” organizacji partnerskiej.
        const defaultOrg =
          orgWsparcia ||
          activeOrgs.find((o) => o.is_default) ||
          activeOrgs.find((o) => o.organization_id !== partnerOrg) ||
          activeOrgs[0] ||
          orgsData[0] ||
          null;

        setCurrentOrganization(defaultOrg ?? null);
      } catch (error) {
        console.error('Error in fetchUserData:', error);
      }
    },
    []
  );

  const refreshUserData = useCallback(async () => {
    const pokolenie = pokolenieSesjiRef.current;
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        await fetchUserData(
          session.user.id,
          pokolenie,
          session.user.email,
          session.user.user_metadata?.full_name
        );
      }
    } catch (error) {
      if (isRefreshTokenError(error)) {
        await resetAuthState('refresh_user_data');
        return;
      }
      throw error;
    }
  }, [fetchUserData, resetAuthState]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      const pokolenie = pokolenieSesjiRef.current;
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Auth initialization timeout')), AUTH_INIT_TIMEOUT_MS);
        });

        const sessionResult = await Promise.race([supabase.auth.getSession(), timeoutPromise]);

        if (cancelled) return;

        const session = sessionResult.data?.session;

        if (session?.user) {
          currentUserIdRef.current = session.user.id;
          await odswiezMfa();
          await fetchUserData(
            session.user.id,
            pokolenie,
            session.user.email,
            session.user.user_metadata?.full_name
          );
        }
      } catch (error) {
        if (cancelled) return;
        if (isRefreshTokenError(error)) {
          await resetAuthState('init_auth');
        } else if (error instanceof DOMException && error.name === 'AbortError') {
          // Swallow Strict Mode remount / navigation aborts
        } else if (error instanceof Error && error.message === 'Auth initialization timeout') {
          console.warn('Auth initialization timed out — continuing without session');
        } else {
          console.error('Error initializing auth:', error);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    initAuth();

    // Fail-safe: never leave the app stuck on the auth spinner. If init or a
    // deferred auth-state fetch stalls (e.g. a hung network/DB call that never
    // settles, so the finally blocks above never run), force the loading state
    // to resolve so routes can render. Any session/org data still populates
    // once the in-flight request settles.
    const loadingFailSafe = setTimeout(() => {
      if (!cancelled) setIsLoading(false);
    }, AUTH_INIT_TIMEOUT_MS);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
        const newUserId = session.user.id;
        const previousUserId = currentUserIdRef.current;
        const isUserChange = previousUserId !== null && previousUserId !== newUserId;
        const isInitialSignIn = previousUserId === null;

        currentUserIdRef.current = newUserId;
        // Wczytywanie poprzedniego użytkownika, które jeszcze leci, nie może
        // nadpisać nowego. Pokolenie bierzemy TUTAJ, synchronicznie, a nie
        // w odroczonym zadaniu niżej — `SIGNED_OUT` przed jego startem musi je
        // unieważnić.
        if (isUserChange) pokolenieSesjiRef.current += 1;
        const pokolenie = pokolenieSesjiRef.current;
        if (isInitialSignIn || isUserChange) {
          setIsLoading(true);
        }

        // Defer DB/auth work out of the onAuthStateChange callback. Awaiting
        // Supabase calls (resetSession / fetchUserData → supabase.from) directly
        // inside this callback can deadlock on the GoTrue auth lock that is held
        // for the callback's duration, which would leave isLoading stuck true
        // (an infinite loading spinner on refresh). setTimeout(0) lets the
        // callback return and release the lock before any query runs, and the
        // finally guarantees the loading state always resolves.
        const { id, email, user_metadata: userMetadata } = session.user;
        setTimeout(async () => {
          if (cancelled) return;
          try {
            if (isUserChange) {
              await resetSession({ reason: 'user_change', isUserChange: true, newUserId });
            }
            await odswiezMfa();
            await fetchUserData(id, pokolenie, email, userMetadata?.full_name);
          } catch (error) {
            console.warn('Failed to fetch user data during auth state change:', error);
          } finally {
            if (!cancelled && (isInitialSignIn || isUserChange)) {
              setIsLoading(false);
            }
          }
        }, 0);
      } else if (event === 'MFA_CHALLENGE_VERIFIED') {
        // Kod przyjęty: token jest już aal2. Poza callbackiem z tego samego
        // powodu, co `fetchUserData` wyżej (blokada GoTrue).
        setTimeout(() => {
          if (!cancelled) void odswiezMfa();
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setStanMfa(BRAK_STANU_MFA);
        // Kolejność siedzi w `runSignOutSequence` — patrz nagłówek tamtego
        // pliku. Skrótowo: stan lokalny czyścimy PRZED sprzątaniem, a samo
        // sprzątanie odraczamy poza ten callback. Wersja z `await resetSession`
        // w tym miejscu trzymała blokadę GoTrue (dokładnie to, przed czym
        // ostrzega komentarz przy `SIGNED_IN` wyżej) i przez czas usuwania
        // kanałów realtime `isAuthenticated` zostawało prawdą, więc
        // `ProtectedRoute` nie przekierowywał na /login.
        runSignOutSequence({
          clearLocalAuthState,
          broadcastSignedOut: () => broadcastAuthEvent({ type: 'signed_out' }),
          teardown: () => resetSession({ reason: 'sign_out', isUserChange: false }),
          defer: (fn) => setTimeout(fn, 0),
          onTeardownError: (error) =>
            console.warn('Session teardown after sign-out failed:', error),
        });
      }
    });

    const unsubscribeBroadcast = subscribeAuthBroadcast(async (event) => {
      if (event.type === 'signed_out') {
        if (currentUserIdRef.current === null) return;
        // Ta sama kolejność co przy własnym wylogowaniu: użytkownik wylogował
        // się w innej karcie, więc TA karta ma natychmiast przestać pokazywać
        // treść chronioną, zamiast czekać na usunięcie kanałów realtime.
        runSignOutSequence({
          clearLocalAuthState,
          // Świadomie NIC nie rozgłaszamy: to jest odbiór cudzego rozgłoszenia,
          // a odpowiedź własnym zapętliłaby karty.
          broadcastSignedOut: () => {},
          teardown: () => resetSession({ reason: 'cross_tab_sign_out', isUserChange: false }),
          defer: (fn) => setTimeout(fn, 0),
          onTeardownError: (error) =>
            console.warn('Session teardown after cross-tab sign-out failed:', error),
        });
      } else if (event.type === 'user_changed') {
        if (event.userId !== currentUserIdRef.current) {
          await resetSession({
            reason: 'cross_tab_user_change',
            isUserChange: true,
            newUserId: event.userId,
          });
          try {
            await supabase.auth.refreshSession();
          } catch {
            // The local session is stale; rely on next auth event to reconcile
          }
        }
      }
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.startAutoRefresh();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(loadingFailSafe);
      subscription.unsubscribe();
      unsubscribeBroadcast();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUserData, resetAuthState, clearLocalAuthState, odswiezMfa]);

  useEffect(() => {
    const orgId = currentOrganization?.organization_id ?? null;
    useModuleSubscriptionStore.getState().setOrganizationId(orgId);
  }, [currentOrganization?.organization_id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    companyData?: CompanyRegistrationData
  ) => {
    const userData: Record<string, unknown> = {
      full_name: fullName,
    };

    if (companyData) {
      userData.nip = companyData.nip;
      userData.company_name = companyData.companyName;
      userData.company_address = companyData.address;
      userData.company_city = companyData.city;
      userData.company_postal_code = companyData.postalCode;
      userData.company_county = companyData.county;
      userData.company_voivodeship = companyData.voivodeship;
      userData.regon = companyData.regon;
      userData.krs = companyData.krs;
      userData.vat_status = companyData.vatStatus;
      userData.vat_api_response = companyData.rawResponse;
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    if (error) throw error;
  };

  /**
   * „Wyloguj" kończy sesję TEGO urządzenia. Domyślny zasięg auth-js to
   * `global` — wylogowanie na telefonie kasowało wtedy sesję desktopu, który
   * dalej trzymał ważny JWT i pokazywał „Unauthorized" pod każdym przyciskiem
   * wtyczki oraz „Offline" na karcie NEXUS (zmierzone 13.09.2026,
   * SESJA_POWLOKI_I_2FA.md §1). Zakończenie sesji wszędzie jest osobną,
   * jawną decyzją użytkownika w ustawieniach (`signOutEverywhere`).
   */
  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
  };

  const signOutEverywhere = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) throw error;
  };

  const switchOrganization = (organizationId: string) => {
    const org = organizations.find((o) => o.organization_id === organizationId);
    if (org) {
      setCurrentOrganization(org);
    }
  };

  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!currentOrganization) return false;

    if (user?.is_super_admin) return true;

    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(currentOrganization.role);
  };

  const canAccessPanel = (panel: 'super-admin' | 'partner' | 'admin'): boolean => {
    if (!user) return false;

    switch (panel) {
      case 'super-admin':
        return user.is_super_admin;
      case 'partner':
        // `partnerOrgId` wpuszcza także członka zespołu partnera (rola
        // employee/manager/admin w organizacji partnerskiej), który nie ma roli
        // `partner`, ale panel partnera jest jego miejscem pracy.
        return (
          user.is_super_admin || organizations.some((o) => o.role === 'partner') || !!partnerOrgId
        );
      case 'admin':
        return user.is_super_admin || organizations.length > 0;
      default:
        return false;
    }
  };

  const [onboardingIncomplete, setOnboardingIncomplete] = useState(false);
  const onboardingMarkedCompleteRef = useRef(false);

  useEffect(() => {
    if (onboardingMarkedCompleteRef.current) return;

    if (!user || !organizationsLoaded || user.is_super_admin) {
      setOnboardingIncomplete(false);
      return;
    }
    if (organizations.length === 0) {
      setOnboardingIncomplete(true);
      return;
    }
    const ownerOrgs = organizations.filter((o) => o.role === 'owner');
    if (ownerOrgs.length === 0) {
      // Ma organizacje, ale w żadnej nie jest właścicielem — pracownik najemcy,
      // pracownik partnera albo partner. Onboarding najemcy zakłada NOWĄ
      // organizację z rolą owner, więc nie wolno go im pokazać: bez tego
      // `else` flaga `true` ustawiona przy 0 organizacji (moment tuż po
      // zalogowaniu, przed przyjęciem zaproszenia) zostawała i wciągała
      // zaproszonego pracownika w kreator zakładania firmy.
      setOnboardingIncomplete(false);
      return;
    }
    // Bramka jest STANEM, nie skutkiem ubocznym (migracja 690, plan
    // GENESIS_JEDNA_PRAWDA_PLAN.md §4.6). Do 13.09.2026 pytała o istnienie
    // wiersza `organization_profiles` — a ten wiersz tworzył research z ekranu
    // „Profil firmy", nie Genesis. Jan-Dar miał graf, profil i strukturę,
    // a formalnie był porzuconym otwarciem do dnia skasowania konta.
    // `onboarding_stan` przychodzi w tym samym zapytaniu co organizacja
    // (`organization:organizations(*)`), więc nie ma tu żadnego I/O.
    const stan = ownerOrgs[0].organization?.onboarding_stan ?? 'gotowa';
    setOnboardingIncomplete(stan !== 'gotowa');
  }, [user, organizationsLoaded, organizations]);

  const markOnboardingComplete = useCallback(() => {
    onboardingMarkedCompleteRef.current = true;
    setOnboardingIncomplete(false);
  }, []);

  const needsOnboarding =
    !!user && organizationsLoaded && onboardingIncomplete && !user.is_super_admin;

  const impersonatePartner = useCallback(
    async (partnerId: string) => {
      if (!user?.is_super_admin) {
        throw new Error('Tylko super-admin może korzystać z trybu podglądu');
      }

      const { data: partner, error: partnerError } = await supabase
        .from('partners')
        .select('id, company_name, organization_id')
        .eq('id', partnerId)
        .single();

      if (partnerError || !partner) {
        throw new Error('Nie znaleziono partnera');
      }

      if (!partner.organization_id) {
        throw new Error(
          'Partner nie ma jeszcze przypisanej organizacji. Najpierw aktywuj partnera.'
        );
      }

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', partner.organization_id)
        .single();

      if (orgError || !org) {
        throw new Error('Nie znaleziono organizacji partnera');
      }

      previousOrgRef.current = currentOrganization;

      const partnerUserOrg: UserOrganization = {
        id: `impersonation-${partner.id}`,
        user_id: user.id,
        organization_id: org.id,
        organization: org,
        role: 'partner',
        status: 'active',
        is_default: false,
      };

      const podglad: ImpersonatingPartner = {
        id: partner.id,
        companyName: partner.company_name,
        organizationId: org.id,
      };
      setImpersonatingPartner(podglad);
      zapiszPodglad(podglad);
      setCurrentOrganization(partnerUserOrg);
    },
    [user, currentOrganization]
  );

  const stopImpersonation = useCallback(() => {
    setImpersonatingPartner(null);
    zapiszPodglad(null);
    if (previousOrgRef.current) {
      setCurrentOrganization(previousOrgRef.current);
    }
    previousOrgRef.current = null;
  }, []);

  /**
   * Odtworzenie podglądu po przeładowaniu karty.
   *
   * `impersonatingPartner` wraca z `sessionStorage` od razu (stan początkowy),
   * ale `currentOrganization` trzeba dociągnąć — i wolno to zrobić dopiero, gdy
   * wiadomo, że zalogowany użytkownik NADAL jest super-adminem. Wpis w
   * sessionStorage jest danymi z przeglądarki, więc sam z siebie nie jest
   * dowodem na uprawnienie; podrobiony wpis u zwykłego użytkownika nie zmieni
   * tu nic ponad baner, bo `partner_kontekst()` po stronie bazy i tak odrzuci
   * podgląd bez roli super-admina.
   */
  const przywrocenieRef = useRef(false);
  useEffect(() => {
    if (przywrocenieRef.current) return;
    if (!impersonatingPartner || !user) return;

    if (!user.is_super_admin) {
      setImpersonatingPartner(null);
      zapiszPodglad(null);
      przywrocenieRef.current = true;
      return;
    }
    if (currentOrganization?.organization_id === impersonatingPartner.organizationId) {
      przywrocenieRef.current = true;
      return;
    }

    przywrocenieRef.current = true;
    (async () => {
      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', impersonatingPartner.organizationId)
        .maybeSingle();

      if (!org) {
        setImpersonatingPartner(null);
        zapiszPodglad(null);
        return;
      }
      previousOrgRef.current = currentOrganization;
      setCurrentOrganization({
        id: `impersonation-${impersonatingPartner.id}`,
        user_id: user.id,
        organization_id: org.id,
        organization: org,
        role: 'partner',
        status: 'active',
        is_default: false,
      });
    })();
  }, [impersonatingPartner, user, currentOrganization]);

  const value: AuthContextType = {
    user,
    organizations,
    currentOrganization,
    isLoading,
    isAuthenticated: !!user,
    organizationsStatus,
    needsOnboarding,
    partnerOrgId,
    impersonatingPartner,
    stanMfa,
    odswiezMfa,
    signIn,
    signUp,
    signOut,
    signOutEverywhere,
    switchOrganization,
    hasRole,
    canAccessPanel,
    refreshUserData,
    markOnboardingComplete,
    impersonatePartner,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
